import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Download } from 'grommet-icons';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { observer } from 'mobx-react-lite';
import { ApiError, ClientService, IncentiveStatementService } from '/src/api';
import {
  Box,
  Button,
  IncentiveStatementDataTable,
  IncentiveStatementDescription,
  IncentiveStatementFuseInfo,
  IncentiveStatementInfoBar,
  Line,
  LoadingSpinner,
  Text,
} from '/src/components';
import { buildIncentiveStatementCsvRows, downloadCsv } from '/src/components/statements/IncentiveStatement/csv';
import { useGlobalStore, useUserStore } from '/src/context';
import { IncentiveStatementResponse, TCountryId, normalizeIncentiveStatementResponse } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { PageNotFound } from '/src/pages';
import { pxToPt } from '/src/utils';

export const IncentiveStatementDetails = observer(() => {
  /** Context **/
  const globalStore = useGlobalStore();
  const userStore = useUserStore();
  const { t } = useTranslation();

  /** Query Params **/
  const params = useParams();
  const clientId = parseInt(params.client_id ?? '');
  const incentiveStatementId = parseInt(params.incentive_statement_id ?? '');
  const reportingPeriodId = parseInt(params.reporting_period_id ?? '');
  const isInvalidParams = Number.isNaN(incentiveStatementId) && Number.isNaN(reportingPeriodId);

  /** State **/
  const [isLoading, setIsLoading] = useState(true);
  const [incentiveStatement, setIncentiveStatement] = useState<IncentiveStatementResponse>();
  const [countryId, setCountryId] = useState<TCountryId>();

  /** Refs **/
  const contentRef = useRef<HTMLDivElement>(null);

  /** Methods **/
  const fetchPageData = async () => {
    try {
      setIsLoading(true);

      if (!Number.isNaN(reportingPeriodId)) {
        const data = await IncentiveStatementService.getIncentiveStatementReportingPeriod({
          id: reportingPeriodId,
        });
        setIncentiveStatement(data);
      } else if (!Number.isNaN(incentiveStatementId)) {
        const data = await IncentiveStatementService.getIncentiveStatement({
          id: incentiveStatementId,
        });
        setIncentiveStatement(data);
      }

      if (!Number.isNaN(clientId)) {
        const client =
          userStore.isClientUser || userStore.isFacilityUser
            ? userStore.user?.clients.find((c) => c.id === clientId)
            : await ClientService.get({ id: clientId });
        setCountryId(client?.country_id);
      }
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.getIncentiveStatement.error);
    } finally {
      setIsLoading(false);
    }
  };

  const generatePDF = async () => {
    if (!contentRef.current || !incentiveStatement) return;

    const normalized = normalizeIncentiveStatementResponse(incentiveStatement);
    const originalElement = contentRef.current;
    const tempElement = originalElement.cloneNode(true) as HTMLElement;

    tempElement.style.width = '1200px';
    tempElement.style.padding = '20px';
    tempElement.style.backgroundColor = 'white';

    const tables = tempElement.querySelectorAll('table');
    tables.forEach((table: HTMLTableElement) => {
      table.style.width = '100%';
      table.style.borderCollapse = 'collapse';
      table.style.marginBottom = '30px';

      const cells = table.querySelectorAll('th, td');
      cells.forEach((cell) => {
        const tableCell = cell as HTMLTableCellElement;
        tableCell.style.padding = '8px';
        tableCell.style.fontSize = '14px';
      });
    });

    tempElement.style.position = 'absolute';
    tempElement.style.left = '-9999px';
    document.body.appendChild(tempElement);

    const contentWidth = tempElement.scrollWidth;
    const contentHeight = tempElement.scrollHeight;

    const canvas = await html2canvas(tempElement, {
      scale: 3,
      logging: false,
      useCORS: true,
      allowTaint: true,
      width: contentWidth,
      height: contentHeight + 100,
    });

    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const marginX = 10;
    const marginY = 10;
    const pageContentWidth = pageWidth - marginX * 2;
    const pageContentHeight = pageHeight - marginY * 2;

    const scale = pageContentWidth / canvas.width;
    const pageHeightPx = pageContentHeight / scale;

    let renderedHeightPx = 0;
    let pageIndex = 0;

    while (renderedHeightPx < canvas.height) {
      const sliceHeightPx = Math.min(pageHeightPx, canvas.height - renderedHeightPx);
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = sliceHeightPx;

      const pageContext = pageCanvas.getContext('2d');
      if (!pageContext) {
        break;
      }

      pageContext.drawImage(
        canvas,
        0,
        renderedHeightPx,
        canvas.width,
        sliceHeightPx,
        0,
        0,
        canvas.width,
        sliceHeightPx
      );

      if (pageIndex > 0) {
        pdf.addPage();
      }

      const sliceHeightMm = sliceHeightPx * scale;
      pdf.addImage(pageCanvas.toDataURL('image/jpeg'), marginX, marginY, pageContentWidth, sliceHeightMm);

      renderedHeightPx += sliceHeightPx;
      pageIndex += 1;
    }

    const safeClientName = normalized.client_name.replace(/\s+/g, '-').toLowerCase();
    const fileId = !Number.isNaN(reportingPeriodId) ? `combined-${reportingPeriodId}` : incentiveStatementId;
    const fileName = `incentive-statement-details-${safeClientName}-${fileId}.pdf`;
    pdf.save(fileName);
    tempElement.remove();
  };

  const generateCsv = () => {
    if (!incentiveStatement) return;

    const normalized = normalizeIncentiveStatementResponse(incentiveStatement);
    const rows = buildIncentiveStatementCsvRows({ incentiveStatement, countryId, t });
    const safeClientName = normalized.client_name
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_-]/g, '');
    const filename = `${safeClientName}_IncentiveStatementDetailed_${dayjs().format('YYYY-MM-DD')}.csv`;

    downloadCsv(rows, filename);
  };

  /** Effects **/
  useEffect(() => {
    if (!Number.isNaN(incentiveStatementId) || !Number.isNaN(reportingPeriodId)) {
      fetchPageData();
    }
  }, [clientId, incentiveStatementId, reportingPeriodId]);

  /** Render **/
  return isInvalidParams ? (
    <PageNotFound />
  ) : !isLoading && incentiveStatement ? (
    <Box>
      <Box
        direction="row"
        justify="center"
        gap={pxToPt('8px')}
        margin={{ top: pxToPt('16px'), bottom: pxToPt('16px') }}
      >
        <Button
          icon={<Download size="small" />}
          label={t('statements:downloadPDF')}
          onClick={generatePDF}
          pad={{ horizontal: '0.5rem', vertical: '0.25rem' }}
        />
        <Button
          icon={<Download size="small" />}
          label={t('statements:downloadCSV')}
          onClick={generateCsv}
          pad={{ horizontal: '0.5rem', vertical: '0.25rem' }}
        />
      </Box>
      <Box fill="horizontal" background="brand" height={pxToPt('8px')} />
      <Box pad={{ horizontal: pxToPt('44px'), vertical: pxToPt('24px') }} fontFamily="Lato, sans-serif">
        <div ref={contentRef}>
          {/** Header **/}
          <Box direction="row" align="center" justify="between" margin={{ bottom: pxToPt('16px') }}>
            <IncentiveStatementFuseInfo />
            <IncentiveStatementDescription />
          </Box>

          {/** Title **/}
          <Box margin={{ bottom: pxToPt('16px') }}>
            <Text size={pxToPt('30px')} weight={700} letterSpacing={pxToPt('-0.4px')}>
              {t('statements:incentiveStatement')}
            </Text>
          </Box>

          {/** Info Bar **/}
          <IncentiveStatementInfoBar
            incentiveStatement={incentiveStatement}
            margin={{ bottom: pxToPt('4px') }}
            pad={{ horizontal: pxToPt('4px') }}
          />

          {/** Line **/}
          <Line margin={pxToPt('14px')} size="xsmall" color="text" />

          {/** Data Table **/}
          <IncentiveStatementDataTable
            incentiveStatement={incentiveStatement}
            countryId={countryId}
            margin={{ bottom: pxToPt('24px') }}
          />
        </div>
      </Box>
    </Box>
  ) : (
    <LoadingSpinner />
  );
});
